import React, { useContext, useMemo } from 'react';
import { Button, Dropdown, MenuProps, Modal, Select, message } from 'antd';
import * as Icons from '@ant-design/icons';
import { Column } from 'react-table';
import { useNavigate } from 'react-router-dom';
import { StopPropagation } from '@shared/components/stop-propagation';
import { nextTableColumns } from '@shared/modules/next-table/helpers/next-table-columns';
import { useAuth } from '@modules/me';
import { ICashRegister } from '../../interfaces';
import { CashRegistersTableContext } from '../../context';
import { CashRegistersService } from '../../services';
import { cashRegisterQueryKeys } from '../../utils';
import { useCurrencies } from '../currencies';

export const useCashRegistersTableColumns = (): Column<ICashRegister>[] => {
  const { handleFetch } = useContext(CashRegistersTableContext);
  const { can } = useAuth();
  const navigate = useNavigate();
  const { data: currencies = [] } = useCurrencies();

  const actionsColumn = useMemo<Column<ICashRegister>>(
    () => ({
      ...nextTableColumns.actions,
      Cell: ({ row: { original } }: any) => {
        const items: MenuProps['items'] = [
          {
            key: 'edit',
            label: 'Düzəliş et',
            icon: <Icons.EditOutlined />,
            disabled: !can('cashboxes.cud'),
            onClick: () => navigate(`/cash-flow/cash-registers/${original.id}/update`, { state: { background: window.location.pathname } }),
          },
          { type: 'divider' as const },
          {
            key: 'delete',
            label: 'Sil',
            icon: <Icons.DeleteOutlined />,
            danger: true,
            disabled: !can('cashboxes.cud'),
            onClick: () =>
              Modal.confirm({
                title: 'Diqqət',
                content: 'Kasanı silməyə əminsinizmi?',
                okType: 'danger',
                onOk: async () => {
                  const result = await CashRegistersService.remove([original.id]);
                  if (result.status === 200) {
                    message.success('Kassa silindi.');
                    handleFetch();
                  } else message.error(result.data as string);
                },
              }),
          },
        ];

        return (
          <StopPropagation>
            <Dropdown menu={{ items }} trigger={['hover']}>
              <Button icon={<Icons.MoreOutlined />} size="small" />
            </Dropdown>
          </StopPropagation>
        );
      },
    }),
    [can, handleFetch, navigate],
  );

  const baseColumns = useMemo<Column<ICashRegister>[]>(
    () => [
      { ...nextTableColumns.small, Header: 'Kod', id: cashRegisterQueryKeys.id, accessor: (r) => r.id },
      { accessor: (r) => r.name, id: cashRegisterQueryKeys.name, Header: 'Ad' },
      { ...nextTableColumns.large, accessor: (r) => r.amount, id: cashRegisterQueryKeys.amount, Header: 'Məbləğ' },
      {
        ...nextTableColumns.small,
        accessor: (r) => r.currency.code,
        id: cashRegisterQueryKeys.currency.id,
        Header: 'Valyuta',
        Filter: ({ column: { filterValue, setFilter } }: any) => (
          <Select allowClear style={{ width: '100%' }} value={filterValue} onChange={setFilter}>
            {currencies.map((c) => (
              <Select.Option key={c.id} value={c.id.toString()}>
                {c.name}
              </Select.Option>
            ))}
          </Select>
        ),
      },
      { ...nextTableColumns.date, accessor: (r) => r.createdAt, id: cashRegisterQueryKeys.createdAt, Header: 'Yaradılma tarixi' },
    ],
    [currencies],
  );

  return useMemo(() => [actionsColumn, ...baseColumns], [actionsColumn, baseColumns]);
};
